import { Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUpdateRecoveryService {
	private readonly retryStorageKey = 'et-infoday:app-update-recovery-retried';
	private initialized = false;
	private retriedThisPageLoad = false;

	constructor(private router: Router) {}

	init(): void {
		if (this.initialized || typeof window === 'undefined') {
			return;
		}

		this.initialized = true;

		this.router.events.pipe(filter((event): event is NavigationError => event instanceof NavigationError)).subscribe((event) => {
			this.recoverFromError(event.error);
		});

		window.addEventListener('unhandledrejection', (event) => {
			if (this.recoverFromError(event.reason)) {
				event.preventDefault();
			}
		});

		window.setTimeout(() => {
			this.clearRetryMarker();
		}, 5000);
	}

	recoverFromError(error: unknown): boolean {
		if (!this.isStaleBuildAssetError(error)) {
			return false;
		}

		if (this.hasRetryMarker()) {
			return false;
		}

		this.setRetryMarker();

		const url = new URL(window.location.href);
		url.searchParams.set('v', Date.now().toString());
		window.location.replace(url.toString());

		return true;
	}

	private isStaleBuildAssetError(error: unknown): boolean {
		const message = this.getErrorMessage(error);

		return [
			/ChunkLoadError/i,
			/Loading chunk .+ failed/i,
			/Failed to fetch dynamically imported module/i,
			/error loading dynamically imported module/i,
			/Importing a module script failed/i,
			/module script.+MIME type.+text\/html/i,
		].some((pattern) => pattern.test(message));
	}

	private getErrorMessage(error: unknown): string {
		if (!error) {
			return '';
		}

		if (typeof error === 'string') {
			return error;
		}

		if (error instanceof Error) {
			return `${error.name}: ${error.message}`;
		}

		if (typeof error === 'object') {
			const maybeError = error as { message?: unknown; reason?: unknown; error?: unknown };

			if (maybeError.message) {
				return this.getErrorMessage(maybeError.message);
			}

			if (maybeError.reason) {
				return this.getErrorMessage(maybeError.reason);
			}

			if (maybeError.error) {
				return this.getErrorMessage(maybeError.error);
			}
		}

		return String(error);
	}

	private hasRetryMarker(): boolean {
		try {
			return this.retriedThisPageLoad || window.sessionStorage.getItem(this.retryStorageKey) !== null;
		} catch {
			return this.retriedThisPageLoad;
		}
	}

	private setRetryMarker(): void {
		this.retriedThisPageLoad = true;

		try {
			window.sessionStorage.setItem(this.retryStorageKey, Date.now().toString());
		} catch {
			// In-memory marker still protects this page load if storage is blocked.
		}
	}

	private clearRetryMarker(): void {
		this.retriedThisPageLoad = false;

		try {
			window.sessionStorage.removeItem(this.retryStorageKey);
		} catch {
			// Storage can be unavailable in restricted browser modes.
		}
	}
}
